
import { useSimpleProfile } from '@/hooks/useSimpleProfile';
import { useOptimizedAuth } from '@/hooks/useOptimizedAuth';
import { useRecentOrders } from '@/hooks/useRecentOrders';
import { useWishlist } from '@/hooks/useWishlist';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useState, useEffect } from 'react';
import { ProfileFormData } from '@/types/profile';
import { Camera, User, Mail, Phone, Calendar, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import ProfileStats from '@/components/profile/ProfileStats';
import RecentOrdersPreview from '@/components/profile/RecentOrdersPreview';
import WishlistPreview from '@/components/profile/WishlistPreview';

const ProfileSettings = () => {
  const { profile, formData, isUpdating, updateProfile, isLoading } = useSimpleProfile();
  const { user } = useOptimizedAuth();
  const { recentOrders } = useRecentOrders(10);
  const { wishlist } = useWishlist();
  const [formState, setFormState] = useState<ProfileFormData>(formData);

  const memberSince = user?.created_at 
    ? format(new Date(user.created_at), 'MMM yyyy')
    : 'N/A';

  useEffect(() => {
    if (profile) {
      setFormState(formData);
    }
  }, [profile, formData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState(prevState => ({ ...prevState, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile(formState);
  };

  if (isLoading) {
    return (
        <Card>
            <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>Update your personal details here.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="space-y-4">
                    <div className="h-10 bg-gray-200 rounded animate-pulse w-1/2"></div>
                    <div className="h-10 bg-gray-200 rounded animate-pulse w-full"></div>
                </div>
                 <div className="space-y-4">
                    <div className="h-10 bg-gray-200 rounded animate-pulse w-1/2"></div>
                    <div className="h-10 bg-gray-200 rounded animate-pulse w-full"></div>
                </div>
            </CardContent>
        </Card>
    )
  }

  const [dateOfBirth, setDateOfBirth] = useState(formState.date_of_birth || '');
  const [gender, setGender] = useState(formState.gender || '');

  return (
    <div className="space-y-8">
      {/* Profile Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <ProfileStats 
          ordersCount={recentOrders.length}
          wishlistCount={wishlist.length}
          memberSince={memberSince}
        />
      </motion.div>

      {/* Profile Picture Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            Profile Picture
          </CardTitle>
          <CardDescription>
            Upload a profile picture to personalize your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div className="relative group">
              <Avatar className="h-24 w-24">
                <AvatarImage src={profile?.avatar_url || ''} />
                <AvatarFallback className="text-lg">
                  {formState.full_name?.split(' ').map(n => n[0]).join('') || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                <Camera className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Button variant="outline" size="sm">
                Upload Photo
              </Button>
              <Button variant="ghost" size="sm" className="text-destructive">
                Remove Photo
              </Button>
              <p className="text-xs text-muted-foreground">
                JPG, PNG or GIF. Max size 5MB.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Personal Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            Personal Information
          </CardTitle>
          <CardDescription>
            Your basic profile information and contact details
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  value={formState.full_name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email Address
                </Label>
                <div className="relative">
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formState.email}
                    disabled
                    className="bg-muted"
                  />
                  <Badge variant="secondary" className="absolute right-2 top-1/2 -translate-y-1/2 text-xs">
                    Verified
                  </Badge>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formState.phone || ''}
                  onChange={handleChange}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gender" className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Gender (Optional)
                </Label>
                <Select value={gender} onValueChange={setGender}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                    <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="date_of_birth" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Date of Birth (Optional)
              </Label>
              <Input
                id="date_of_birth"
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                name="bio"
                value={formState.bio || ''}
                onChange={handleChange}
                rows={4}
                placeholder="Tell us a little about yourself..."
              />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex gap-2 pt-4"
            >
              <Button type="submit" disabled={isUpdating} loading={isUpdating} className="flex-1">
                Save Changes
              </Button>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </motion.div>
          </form>
        </CardContent>
      </Card>

      {/* Account Status */}
      <Card className="border-green-200 bg-green-50/50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-green-900">Account Status</h4>
              <p className="text-sm text-green-700">Your account is active and verified</p>
            </div>
            <Badge className="bg-green-100 text-green-800">
              Verified
            </Badge>
          </div>
                </CardContent>
            </Card>

            {/* Recent Activity Previews */}
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-8"
            >
                <RecentOrdersPreview />
                <WishlistPreview />
            </motion.div>
        </div>
    );
};

export default ProfileSettings;
